/**
 * @module Experince/Product-Selector
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { ItemDetail } from '@support/api/common/product.interface';
import { CartItem, CartLine } from '@support/api/proxies/responses/api-cart-response.interface';
import { CartResponse } from '@experience/shop/product-selector/interfaces/product-selector.interface';
import { NewSimData } from '@experience/shop/byod/interfaces/byod-landing.interface';
import { PRODUCT_SELECTOR_MODEL_CONSTANTS } from '@experience/shop/product-selector/models/product-selector-model-constants';

/** */

export class MaptoCartResponseModel {
  constructor() {}

  /**
   * Function used to map to plans object coming from cart response
   * @param {CartLine} item
   * @param {number} index
   * @param {ObjectService} objectService
   * @returns {CartResponse}
   */
  public static mapToPlans(
    item: CartLine,
    index: number,
    objectService: ObjectService
  ): CartResponse {
    return {
      planOfferName: objectService.get(item, 'plan.0.offerName'),
      planDescription: objectService.get(item, 'plan.0.description'),
      salesPrice: objectService.get(
        item,
        'plan.0.price.priceSummary.monthlyPrice.salePrice.amount'
      ),
      displayPrice: objectService.get(
        item,
        'plan.0.price.priceSummary.payNowPrice.salePrice.amount'
      ),
      offerCode: objectService.get(item, 'plan.0.offerId'),
      id: objectService.get(item, 'id'),
      nickName: objectService.get(item, 'lineName') || 'Line' + (index + 1).toString(),
      familyCode: objectService.get(item, 'plan.0.familyId'),
      cartItemId: objectService.get(item, 'plan.0.id'),
    };
  }

  /**
   * Function to map cart level or line level device details with cart response if product subtype is Handset
   * @param {CartItem} device
   */
  public static mapToDevices(
    device: CartItem,
    objectService: ObjectService,
    productOffers?: ItemDetail
  ): ItemDetail {
    // HTML is expecting following Interface for skuPrice. So it is required.
    return {
      skuPrice: [
        {
          priceSummary: {
            payNowPrice: {
              salePrice: objectService.get(device, 'price.priceSummary.payNowPrice.salePrice'),
              listPrice: objectService.get(device, 'price.priceSummary.payNowPrice.listPrice'),
            },
          },
        },
      ],
      canonicalUrl: objectService.get(device, 'canonicalUrl'),
      memory: objectService.get(device, 'memory'),
      description: objectService.get(device, 'description'),
      color: objectService.get(device, 'color'),
      cartItemId: objectService.get(device, 'id'),
      familyName: objectService.get(device, 'offerName'),
      offerId: objectService.get(device, 'offerId'),
      mainImageUrl: objectService.get(device, 'imageURL'),
      sku: objectService.get(device, 'sku'),
      serviceThreshold: objectService.get(productOffers, 'serviceThreshold') || 0,
      priceWithoutService: objectService.get(productOffers, 'priceWithoutService') || 0,
      priceWithService: objectService.get(productOffers, 'priceWithService') || 0,
      // @TODO remove followin key after modifying PDP page
      id: objectService.get(device, 'sku'),
      skuImageUrl:
        objectService.get(productOffers, 'skuImageURL') || objectService.get(device, 'skuImageURL'),
      hasDeal: objectService.get(productOffers, 'hasDeal'),
      hasSale: objectService.get(productOffers, 'hasSale'),
      availabilityStatus:
        objectService.get(device, 'availability') ||
        objectService.get(productOffers, 'availabilityStatus'),
      estimatedShippingFromDateTime: objectService.get(device, `estimatedShippingFromDateTime`),
      estimatedShippingToDateTime: objectService.get(device, `estimatedShippingToDateTime`),
      itemPromotions: objectService.get(productOffers, 'itemPromotions'),
      isCPOAvailable: objectService.get(device, 'hasCPO'),
      isNewAvailable: objectService.get(device, 'hasNew'),
      listPrice: objectService.get(device, 'price.priceSummary.payNowPrice.listPrice.amount'),
      salePrice: objectService.get(device, 'price.priceSummary.payNowPrice.salePrice.amount'),
      productPrice: objectService.get(device, 'hasSale')
        ? objectService.get(device, 'price.priceSummary.payNowPrice.salePrice.amount')
        : objectService.get(device, 'price.priceSummary.payNowPrice.listPrice.amount'),
      productSubType: objectService.get(device, 'productSubType.0'),
      isConditionNew: objectService.get(device, 'condition')
        ? objectService.get(device, 'condition').toLocaleLowerCase() ===
          PRODUCT_SELECTOR_MODEL_CONSTANTS.NEW
        : false,
      legalText: objectService.get(productOffers, 'legalText'),
      actionCode: objectService.get(device, 'actionCode'),
      cartItemStatus: objectService.get(device, 'cartItemStatus'),
      uSkuIndicator: objectService.get(device, 'uSkuIndicator'),
    };
  }
  /**
   * Function used to map to sim kit. I need a sim scenario
   */
  public static mapToSimKit(
    devicesFromCart: CartItem,
    simNumber: string,
    objectService: ObjectService
  ): NewSimData {
    return {
      cartItemId: objectService.get(devicesFromCart, 'id'),
      simSKU: objectService.get(devicesFromCart, 'sku'),
      listPrice: objectService.get(
        devicesFromCart,
        'price.priceSummary.payNowPrice.listPrice.amount'
      ),
      salesPrice: objectService.get(
        devicesFromCart,
        'price.priceSummary.payNowPrice.salePrice.amount'
      ),
      simDescription: objectService.get(devicesFromCart, 'offerName'),
      simNumber,
    };
  }
}
